/**
 * The question `ssh` asks on first contact, raised in the app instead of on a tty.
 *
 * A machine whose key is not on record cannot be verified against anything, so `ssh` prints its
 * fingerprint and waits to be told whether that is the machine the person meant. Accepting records
 * the key; declining ends the attempt. Nothing here is a secret — the fingerprint is public, and
 * showing it in full is the entire point.
 *
 * Only the main process raises these: it is the one running `ssh`. The renderer displays them and
 * sends the answer back.
 */

export interface SshHostKeyPromptRequest {
  readonly requestId: string;
  readonly destination: string;
  readonly keyType: string;
  readonly fingerprint: string;
  readonly expiresAtMs: number;
}

const pending = new Map<string, SshHostKeyPromptRequest>();
const listeners = new Set<(requests: ReadonlyArray<SshHostKeyPromptRequest>) => void>();

function publish(): void {
  const requests = [...pending.values()];
  for (const listener of listeners) listener(requests);
}

export function subscribeSshHostKeyPrompts(
  listener: (requests: ReadonlyArray<SshHostKeyPromptRequest>) => void,
): () => void {
  listeners.add(listener);
  listener([...pending.values()]);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Answers one question. The main process owns the decision's consequences, so this only reports
 * it: there is nothing to undo here if the reply never lands.
 */
export function resolveSshHostKeyPrompt(requestId: string, accepted: boolean): void {
  const entry = pending.get(requestId);
  if (entry === undefined) return;
  pending.delete(requestId);
  publish();
  void window.desktopBridge?.ssh?.resolveHostKeyPrompt(requestId, accepted);
}

/**
 * Shows the questions the main process raises while opening a forward.
 *
 * Its own timeout is authoritative; the one kept here is a floor, so a dialog cannot outlive a
 * question the main process has already given up on.
 */
export function attachDesktopSshHostKeyPrompts(): () => void {
  const ssh = window.desktopBridge?.ssh;
  if (ssh === undefined || ssh.onHostKeyPrompt === undefined) return () => {};
  return ssh.onHostKeyPrompt((request) => {
    const expiresAtMs = Date.parse(request.expiresAt);
    pending.set(request.requestId, {
      requestId: request.requestId,
      destination: request.destination,
      keyType: request.keyType,
      fingerprint: request.fingerprint,
      expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now() + 120_000,
    });
    publish();
  });
}

/** Clears every open question, for a teardown that invalidates them all. */
export function cancelAllSshHostKeyPrompts(): void {
  for (const requestId of Array.from(pending.keys())) {
    resolveSshHostKeyPrompt(requestId, false);
  }
}
